import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "../api/ApiService";

interface MovieFormData {
  movie1: string;
  movie2: string;
  movie3: string;
  movie4: string;
  movie5: string;
  movie6: string;
  movie7: string;
  movie8: string;
  movie9: string;
  movie10: string;
}

interface MovieListResponseItem {
  id: string;
  createdBy: string;
  movieList: string[];
}

interface MovieListResponse {
  statusCode: number;
  data?: MovieListResponseItem[];
  message?: string;
}

const MoviesFormPage = () => {

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [id, setId] = useState<string>("");
  const [createdBy, setCreatedBy] = useState<string>("");
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [formData, setFormData] = useState<MovieFormData>({
    movie1: "",
    movie2: "",
    movie3: "",
    movie4: "",
    movie5: "",
    movie6: "",
    movie7: "",
    movie8: "",
    movie9: "",
    movie10: "",
  });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const fetchMyMovieList = async () => {
      try {
        const response: MovieListResponse = await ApiService.getMyMovieList();

        if (response.statusCode === 200) {
          const entry = response.data?.[0];

          if (entry?.movieList.length) {
            setIsEdit(true);
            setId(entry.id);
            setCreatedBy(entry.createdBy);

            const movieList = entry.movieList;

            setFormData((prev) => {
              const updated = { ...prev };
              movieList.forEach((title, index) => {
                updated[`movie${index + 1}` as keyof MovieFormData] = title;
              });
              return updated;
            });
          }
        } else {
          setError(response.message || "Failed to fetch movie list");
        }
      } catch (err: any) {
        setError(err.response?.data?.message || "Error fetching movie list");
      }
    };

    fetchMyMovieList();
  }, []);


  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name as keyof MovieFormData]: value,
    }));
  };

  const handleCreate = async () => {
    setError("");

    if (!formData.movie1) {
      setError("Please enter at least 1 movie");
      scrollToTop();
      return;
    }

    try {
      setIsLoading(true);
      const moviesArr: string[] = Object.values(formData).filter(
        (movie) => movie.trim() !== ""
      );

      await ApiService.createMovieList({ movieList: moviesArr });
      setIsLoading(false);

      navigate("/all-movies", {
        state: { fromSource: "Movie list created successfully!" },
      });
      scrollToTop();
    } catch (err: any) {
      setError(err.response?.data?.message || "Error creating movie list");
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    setError("");

    if (!formData.movie1) {
      setError("Please enter at least 1 movie");
      scrollToTop();
      return;
    }

    try {
      setIsLoading(true);
      const moviesArr: string[] = Object.values(formData).filter(
        (movie) => movie.trim() !== ""
      );

      await ApiService.updateMovieList({ id, createdBy, movieList: moviesArr });
      setIsLoading(false);

      navigate("/all-movies", {
        state: { fromSource: "Movie list updated successfully!" },
      });
      scrollToTop();
    } catch (err: any) {
      setError(err.response?.data?.message || "Error updating movie list");
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this movie list?"
    );

    if (confirmDelete) {
      try {
        setIsLoading(true);
        const moviesArr: string[] = Object.values(formData).filter(
          (movie) => movie.trim() !== ""
        );

        await ApiService.deleteMovieList({ id, createdBy, movieList: moviesArr });
        setIsLoading(false);

        navigate("/all-movies", {
          state: { fromSource: "Movie list deleted successfully!" },
        });
        scrollToTop();
      } catch (err: any) {
        setError(err.response?.data?.message || "Error deleting movie list");
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="movie-form-container">
      <h2>{isEdit ? "Edit My Movie List" : "Create My Movie List"}</h2>
      <p>Enter your top 10 favorite movies of all time!</p>
      {error && <div className="error-message">{error}</div>}

      <form>
        {Object.keys(formData).map((key, index) => (
          <div className="form-group" key={key}>
            <input
              type="text"
              id={key}
              name={key}
              value={formData[key as keyof MovieFormData]}
              onChange={handleChange}
              placeholder={`Enter Movie ${index + 1}`}
            />
          </div>
        ))}

        <div className="form-actions">
          {isEdit ? (
            <>
              <button type="button" className="save-button" onClick={handleUpdate}>
                Update Movie List
              </button>
              <button type="button" className="delete-button" onClick={handleDelete}>
                Delete Movie List
              </button>
              <button type="button" className="cancel-button" onClick={() => navigate("/")}>
                Cancel Edit
              </button>
            </>
          ) : (
            <button type="button" className="save-button" onClick={handleCreate}>
              Create Movie List
            </button>
          )}
        </div>

        {isLoading && <div className="spinner"></div>}
      </form>
    </div>
  );
};

export default MoviesFormPage;
